'use client'

import { C, Callout, Exercise, H, Lesson, M, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/scale-extra'

export default function ProjectYourGpt() {
  return (
    <Lesson>
      <Step>
        <H>Project: train your own GPT</H>
        <P>
          This is the big one: a complete, honest language-model training run, built from parts you understand. The model is
          the GPT from this module. Everything around it is yours: the tokenizer, the data split, the schedule, clipping,
          validation, sampling.
        </P>
        <OL>
          <li>Tokenize your text, split it, and set a baseline.</li>
          <li>Train with warm-up, cosine decay and gradient clipping, tracking validation loss.</li>
          <li>Generate text with temperature and top-k, and measure perplexity.</li>
        </OL>
        <Callout kind="try">
          The default text is the course’s short stories. Paste anything you like instead: song lyrics, recipes, your own
          writing, code. A few thousand characters is enough to see it learn your text’s style.
        </Callout>
      </Step>

      <Step gate="gpt-data">
        <H>Part 1 · Tokens and a baseline</H>
        <P>
          Character-level tokens keep things simple: the vocabulary is every distinct character. Split <em>by position</em>,
          not at random: train on the first 90% and validate on the last 10%. That way the model is tested on a stretch of text
          it has never seen.
        </P>
        <Exercise
          id="gpt-data"
          title="tokenizer and split"
          {...EX.gptData}
          hints={[
            <>
              <C>{'const chars = [...new Set(text)].sort()'}</C>, <C>{'encode = s => [...s].map(c => chars.indexOf(c))'}</C>,{' '}
              <C>{"decode = ids => ids.map(i => chars[i]).join('')"}</C>.
            </>,
            <>
              <C>{'const cut = Math.floor(ids.length * 0.9)'}</C>, then <C>ids.slice(0, cut)</C> and <C>ids.slice(cut)</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="gpt-train">
        <H>Part 2 · The training run</H>
        <P>
          Bring in the tools from the training-at-scale lesson. The model is about 27 000 parameters, so the run takes a few
          seconds. Watch both curves: training loss keeps falling, but validation loss is the honest one.
        </P>
        <Exercise
          id="gpt-train"
          timeoutMs={90000}
          outputHeight={420}
          title="lrAt, clipGrads, valLoss and the loop"
          {...EX.gptTrain}
          hints={[
            <>
              Copy <C>lrAt</C> from the training-at-scale lesson. For clipping: sum <C>g * g</C> over every <C>p.grad</C>; if the
              square root is above <C>maxNorm</C>, multiply every entry by <C>maxNorm / norm</C>.
            </>,
            <>
              valLoss: for <C>{'i = 0, T, 2T, …'}</C> while <C>{'i + T + 1 < valIds.length'}</C>, add{' '}
              <C>{'gpt.forward(valIds.slice(i, i + T)).crossEntropy(valIds.slice(i + 1, i + T + 1)).item()'}</C>, and average.
            </>,
            <>
              Each step: set <C>opt.lr</C>; build <C>loss</C> as the average of 4 windows (<C>{'.mul(0.25)'}</C> each); then{' '}
              <C>zeroGrad</C>, <C>backward</C>, <C>clipGrads</C>, <C>step</C>. Every 50 steps, <C>{'chart.push({ train, validation })'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="gpt-generate">
        <H>Part 3 · Make it write</H>
        <P>
          Two controls shape the output. <strong>Temperature</strong> flattens or sharpens the probabilities.{' '}
          <strong>Top-k</strong> throws away all but the k most likely tokens, so a single unlucky pick can’t derail the text.
          And one number sums up the model: <strong>perplexity</strong>, <M>{'e^{\\text{loss}}'}</M>, which is roughly how many
          characters the model is still choosing between at each step.
        </P>
        <Exercise
          id="gpt-generate"
          timeoutMs={90000}
          outputHeight={360}
          title="generate and perplexity"
          {...EX.gptGenerate}
          hints={[
            <>
              <C>{'const ctx = s.slice(-T); const scores = gpt.forward(ctx).row(ctx.length - 1)'}</C>
            </>,
            <>
              The k-th largest score is <C>{'[...scores].sort((a, b) => b - a)[topK - 1]'}</C>; replace everything below it with{' '}
              <C>-Infinity</C>, then <C>{'random.sample(vec.softmax(kept, temperature))'}</C>.
            </>,
            <>
              <C>{'const perplexity = Math.exp(valLoss())'}</C>
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Module 9 complete — you built a GPT</H>
        <P>
          Tokenizer, embeddings, attention, residual blocks, layer norm, a training loop with warm-up, decay and clipping, a
          validation curve, and a sampler with temperature and top-k. That’s the full recipe behind GPT-style models. What
          separates yours from the frontier is mostly scale: more parameters, more data, more compute, and a lot of engineering.
        </P>
        <UL>
          <li>Stretch: make it bigger (C = 48, three blocks). Does validation loss improve, or does it overfit this little text?</li>
          <li>Stretch: train on your own text, and compare its perplexity with the stories. Which is easier to predict, and why?</li>
          <li>Stretch: turn off clipping and warm-up and raise the learning rate. Can you make training spike?</li>
        </UL>
      </Step>
    </Lesson>
  )
}
